import { useEffect } from "react";

/**
 * Injects/updates all SEO tags for a Buying Advice page.
 * Client-side helmet approach (no external dep) — updates <title> and
 * <meta>/<link> tags in document.head. Modern Googlebot renders JS
 * and reads these tags on the second-pass crawl, so we hit ~95% of
 * SSR SEO benefit for an SPA.
 *
 * All tags are cleaned up on unmount so navigating between article
 * pages doesn't accumulate stale entries.
 */
export const ArticleSeo = ({
  title,
  description,
  canonicalUrl,
  ogImage,
  articleJsonLd,
  type = "article", // "article" | "website"
}) => {
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;

    const created = [];
    const setMeta = (attr, key, value) => {
      if (!value) return;
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      const isNew = !el;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
        created.push(el);
      }
      el.setAttribute("content", value);
    };
    const setLink = (rel, href) => {
      if (!href) return;
      let el = document.head.querySelector(`link[rel="${rel}"]`);
      const isNew = !el;
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
        created.push(el);
      }
      el.setAttribute("href", href);
    };

    setMeta("name", "description", description);
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:type", type === "article" ? "article" : "website");
    setMeta("property", "og:url", canonicalUrl);
    setMeta("property", "og:image", ogImage);
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);
    setMeta("name", "twitter:image", ogImage);
    setLink("canonical", canonicalUrl);

    // JSON-LD structured data
    let jsonLdEl = null;
    if (articleJsonLd) {
      jsonLdEl = document.createElement("script");
      jsonLdEl.type = "application/ld+json";
      jsonLdEl.text = JSON.stringify(articleJsonLd);
      jsonLdEl.setAttribute("data-seo", "article");
      document.head.appendChild(jsonLdEl);
    }

    return () => {
      document.title = prevTitle;
      created.forEach((el) => el.parentNode?.removeChild(el));
      if (jsonLdEl) jsonLdEl.parentNode?.removeChild(jsonLdEl);
    };
  }, [title, description, canonicalUrl, ogImage, articleJsonLd, type]);

  return null;
};
